/**
 * One shared marker layout for the price pane: no two per-bar glyphs overlap,
 * whichever primitive drew them.
 *
 * lightweight-charts asks every primitive for its renderer and draws them one
 * after another, so no primitive can see what the others are about to draw.
 * The registry closes that gap. Each glyph primitive registers a PROVIDER, a
 * function that returns the boxes it wants for the current view (stable key,
 * bar x, side, anchor y, size, priority). The first renderer of a frame that
 * asks for positions runs `layout()`, which pulls every provider and places
 * every box at once; the result is cached until the view or an item set
 * changes, so the other renderers (and the crosshair-only repaints, which redraw
 * the whole pane) read the same answer.
 *
 * Placement (`computeMarkerLayout`, pure, unit tested):
 *  1. Fixed boxes first (trade fills, BOS labels on their line, the news lane):
 *     they never move. One that would overlap an earlier fixed box collapses.
 *  2. Free boxes bar by bar, oldest first; within a bar by priority (highest
 *     closest to the candle), then source rank, then key. Each starts at its
 *     preferred slot (anchor +- gap, outward) and slides outward past every box
 *     already placed that it overlaps horizontally, neighbouring bars included,
 *     so a 24px glyph on a 4px bar spacing clears its neighbours' glyphs too.
 *     Oldest first is what keeps the chart still while it runs live: a marker
 *     on the newest bar is placed after everything older and can never move it.
 *  3. A box pushed further than `maxStack` from its preferred slot collapses
 *     into a "+N" chip on that bar and side (or into a chip on a neighbouring
 *     bar it would sit on top of); the chip is a box like any other, and its
 *     hover lists what it hides.
 *
 * Candles are obstacles too: every visible bar's high-low span (body width)
 * goes in with the fixed boxes, so a free marker never paints on a candle.
 * A marker tied to a price inside the bar (a flow signal, say) starts its
 * stack at the bar's high or low like the rest, and a thin dotted LEADER runs
 * from the exact anchor (bar x, price y) to the near edge of its box; any free
 * box pushed off its preferred slot, and every chip, gets one back to its bar.
 *
 * Coordinates are CSS (media) px, y down. Primitives draw at the box the layout
 * gives them; hit-testing reads the same boxes.
 */

export type LayoutSide = 'above' | 'below';

export interface LayoutItem {
  /** Unique within its source and stable across renders. */
  key: string;
  /** Bar time, unix seconds (chips group by it). */
  time: number;
  /** Bar centre x, px. */
  x: number;
  /** Above: stacks upward from the anchor. Below: downward. */
  side: LayoutSide;
  /**
   * Free box: the y it stacks away from (candle high/low, or the signal's own
   * price). Fixed box: its vertical centre.
   */
  anchorY: number;
  /** Box size, px. */
  w: number;
  h: number;
  /** Box left edge relative to `x` (default -w/2, centred). */
  dx?: number;
  /** Px between the anchor and the box's near edge when nothing is in the way (default 2). */
  gap?: number;
  /** Higher sits closer to the candle; fixed boxes win ties among themselves by it. */
  priority: number;
  /** Never moved (drawn at an exact price); free boxes flow around it. */
  fixed?: boolean;
  /** What a "+N" chip's tooltip says for this item. */
  label?: string;
  /** Opaque payload handed back to the source with its placement. */
  ref?: unknown;
  /**
   * The exact point the marker is about (bar x, this y), when it is not the
   * stack's start: a signal price inside the bar. The leader starts here.
   */
  leaderY?: number;
  /** Leader colour (the marker's hue); defaults to slate. */
  color?: string;
  /** The source draws its own connector for this box (no layout leader). */
  noLeader?: boolean;
}

/** A dotted connector from the marker's anchor to its box. */
export interface LayoutLeader {
  source: string;
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
}

/** A visible candle as the layout sees it: its bar x, high/low y and half body width. */
export interface LayoutCandle {
  time: number;
  x: number;
  highY: number;
  lowY: number;
  halfW: number;
}

export interface LayoutBox {
  left: number;
  top: number;
  w: number;
  h: number;
}

export interface PlacedItem extends LayoutBox {
  source: string;
  item: LayoutItem;
}

export interface HiddenRef {
  source: string;
  key: string;
  label: string;
  time: number;
}

export interface LayoutChip extends LayoutBox {
  key: string;
  time: number;
  side: LayoutSide;
  x: number;
  hidden: HiddenRef[];
}

export interface LayoutResult {
  /** Every placed box, in placement order. */
  placed: PlacedItem[];
  bySource: Map<string, PlacedItem[]>;
  chips: LayoutChip[];
  /** Dotted connectors, drawn under the markers. */
  leaders: LayoutLeader[];
  /** The candle spans the free boxes were kept off. */
  candles: LayoutBox[];
}

export const LEADER_COLOR = 'rgba(148, 163, 184, 0.9)';
/** A leader is only drawn when the box sits further than gap + this from its anchor. */
export const LEADER_MIN = 2;

/** A candle's obstacle box: its whole high-low span, body wide (at least the 1px wick). */
export function candleBox(c: LayoutCandle): LayoutBox {
  const half = Math.max(0.5, c.halfW);
  const top = Math.min(c.highY, c.lowY);
  return { left: c.x - half, top, w: half * 2, h: Math.max(1, Math.abs(c.lowY - c.highY)) };
}

/** Half the drawn candle body for a bar spacing (lightweight-charts draws ~0.8 of the spacing). */
export function candleHalfWidth(barSpacing: number): number {
  return Math.max(0.5, Math.floor(barSpacing * 0.8) / 2);
}

export interface LayoutOptions {
  /** How far (px) a free box may be pushed from its preferred slot before it collapses. */
  maxStack: number;
  /** Minimum vertical clearance between two boxes, px. */
  pad: number;
  chipW: number;
  chipH: number;
}

export interface LayoutSourceInput {
  id: string;
  /** Tie-break between sources (lower first); see SOURCE_RANK. */
  rank: number;
  items: readonly LayoutItem[];
}

export const DEFAULT_LAYOUT_OPTIONS: LayoutOptions = { maxStack: 120, pad: 1, chipW: 28, chipH: 14 };
export const DEFAULT_GAP = 2;
export const CHIP_FONT = 'bold 10px Arial';

/**
 * Source ids and their tie-break order. Priority (per item) decides who sits
 * closest to the candle; this only orders equal priorities, so a re-render
 * never flips two sources.
 */
export const SOURCE_RANK: Record<string, number> = {
  trades: 0,
  bos: 1,
  news: 2,
  fairPrice: 3,
  signal: 4,
  seriesMarkers: 5,
  flow: 6,
  absorption: 7,
  candleSpike: 8,
  volume: 9,
  skipped: 10,
  donchian: 11,
  trailingPullback: 12,
  displacement: 13,
  magnet: 14,
  candidate: 15,
  levelTitles: 16,
};

/**
 * Priorities used by the integrated sources: the closest-to-the-candle order
 * when they share a bar side.
 */
export const LAYOUT_PRIORITY = {
  fixed: 100,
  fairPriceTriangle: 95,
  signalDot: 90,
  seriesMarker: 85,
  flow: 80,
  absorption: 75,
  candleSpike: 70,
  volume: 65,
  skipped: 60,
  donchian: 55,
  trailingPullback: 55,
  fairPriceChip: 50,
  displacement: 45,
  magnet: 40,
  signalText: 35,
  candidate: 30,
} as const;

// ------------------------------------------------------------- core layout

interface Entry {
  source: string;
  rank: number;
  item: LayoutItem;
}

interface FlowBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
  /** Bar x it was placed for (the scan window keys on it). */
  bx: number;
}

const EMPTY_PLACED: readonly PlacedItem[] = Object.freeze([]) as readonly PlacedItem[];

function cmpKey(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function boxLeft(it: LayoutItem): number {
  return it.x + (it.dx ?? -it.w / 2);
}

/** Top of the preferred slot: the near edge `gap` off the anchor. */
function preferredTop(it: LayoutItem): number {
  const gap = it.gap ?? DEFAULT_GAP;
  return it.side === 'below' ? it.anchorY + gap : it.anchorY - gap - it.h;
}

export function boxesIntersect(a: LayoutBox, b: LayoutBox): boolean {
  return a.left < b.left + b.w && b.left < a.left + a.w && a.top < b.top + b.h && b.top < a.top + a.h;
}

// Scratch, reused between calls (the layout is synchronous).
const scratch: FlowBox[] = [];

/**
 * Place every source's items. Pure: the same input always gives the same
 * output, and the output never has two intersecting boxes (items and chips).
 */
export function computeMarkerLayout(
  sources: readonly LayoutSourceInput[],
  opts: LayoutOptions = DEFAULT_LAYOUT_OPTIONS,
  candles: readonly LayoutCandle[] = [],
): LayoutResult {
  const fixed: Entry[] = [];
  const free: Entry[] = [];
  for (const s of sources) {
    for (const item of s.items) {
      if (!Number.isFinite(item.x) || !Number.isFinite(item.anchorY) || !(item.w > 0) || !(item.h > 0)) continue;
      (item.fixed ? fixed : free).push({ source: s.id, rank: s.rank, item });
    }
  }
  fixed.sort((a, b) => b.item.priority - a.item.priority || a.item.time - b.item.time || a.rank - b.rank || cmpKey(a.item.key, b.item.key));
  free.sort(
    (a, b) =>
      a.item.x - b.item.x ||
      a.item.time - b.item.time ||
      b.item.priority - a.item.priority ||
      a.rank - b.rank ||
      cmpKey(a.item.key, b.item.key),
  );

  const placed: PlacedItem[] = [];
  const bySource = new Map<string, PlacedItem[]>();
  const chips: LayoutChip[] = [];
  const chipsByKey = new Map<string, LayoutChip>();
  const overflow: Entry[] = [];
  const leaders: LayoutLeader[] = [];
  const candleBoxes: LayoutBox[] = candles.map(candleBox);
  const pad = opts.pad;

  /** Leader from (x, fromY) to the near edge of a box on `side`, when it is far enough to need one. */
  const lead = (source: string, key: string, x: number, fromY: number, gap: number, side: LayoutSide, b: LayoutBox, color: string | undefined): void => {
    const x2 = Math.min(Math.max(x, b.left), b.left + b.w);
    const y2 = side === 'below' ? b.top : b.top + b.h;
    if (Math.abs(y2 - fromY) <= gap + LEADER_MIN) return;
    leaders.push({ source, key, x1: x, y1: fromY, x2, y2, color: color ?? LEADER_COLOR });
  };

  const record = (e: Entry, left: number, top: number): void => {
    const p: PlacedItem = { source: e.source, item: e.item, left, top, w: e.item.w, h: e.item.h };
    if (!e.item.fixed && !e.item.noLeader) {
      const it = e.item;
      lead(e.source, it.key, it.x, it.leaderY ?? it.anchorY, it.leaderY !== undefined ? -LEADER_MIN : it.gap ?? DEFAULT_GAP, it.side, p, it.color);
    }
    placed.push(p);
    let list = bySource.get(e.source);
    if (!list) bySource.set(e.source, (list = []));
    list.push(p);
  };

  // 1. Fixed boxes: exact, first come (by priority) first served.
  const fixedBoxes: FlowBox[] = [];
  for (const e of fixed) {
    const left = boxLeft(e.item);
    const top = e.item.anchorY - e.item.h / 2;
    const b: FlowBox = { left, top, right: left + e.item.w, bottom: top + e.item.h, bx: e.item.x };
    let hit = false;
    for (const f of fixedBoxes) {
      if (b.left < f.right && f.left < b.right && b.top < f.bottom && f.top < b.bottom) {
        hit = true;
        break;
      }
    }
    if (hit) {
      overflow.push(e);
      continue;
    }
    fixedBoxes.push(b);
    record(e, left, top);
  }
  fixedBoxes.sort((a, b) => a.left - b.left);
  let maxFixedW = 0;
  for (const f of fixedBoxes) maxFixedW = Math.max(maxFixedW, f.right - f.left);

  // 2. Free boxes, left to right.
  const flow: FlowBox[] = [];
  let maxReach = 0;

  /** Boxes that overlap [L, R) horizontally, into `scratch`. */
  const collect = (L: number, R: number): FlowBox[] => {
    scratch.length = 0;
    // Fixed: sorted by left; first candidate is the first with left >= L - maxFixedW.
    let lo = 0;
    let hi = fixedBoxes.length;
    const from = L - maxFixedW;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (fixedBoxes[mid].left < from) lo = mid + 1;
      else hi = mid;
    }
    for (let i = lo; i < fixedBoxes.length; i += 1) {
      const f = fixedBoxes[i];
      if (f.left >= R) break;
      if (f.right > L) scratch.push(f);
    }
    // Flow: placement order is by bar x, so scan back until no box can reach L.
    for (let i = flow.length - 1; i >= 0; i -= 1) {
      const f = flow[i];
      if (f.bx + maxReach < L) break;
      if (f.left < R && L < f.right) scratch.push(f);
    }
    return scratch;
  };

  /** Top of the first free slot at or beyond `prefTop`, sliding outward. */
  const slot = (L: number, R: number, prefTop: number, h: number, side: LayoutSide): number => {
    const blockers = collect(L, R);
    if (side === 'below') {
      blockers.sort((a, b) => a.top - b.top);
      let cur = prefTop;
      for (const b of blockers) {
        if (b.bottom + pad <= cur) continue;
        if (b.top >= cur + h + pad) break;
        cur = b.bottom + pad;
      }
      return cur;
    }
    blockers.sort((a, b) => b.bottom - a.bottom);
    let bottom = prefTop + h;
    for (const b of blockers) {
      if (b.top >= bottom + pad) continue;
      if (b.bottom + pad <= bottom - h) break;
      bottom = b.top - pad;
    }
    return bottom - h;
  };

  const pushFlow = (left: number, top: number, w: number, h: number, bx: number): void => {
    flow.push({ left, top, right: left + w, bottom: top + h, bx });
    maxReach = Math.max(maxReach, Math.abs(left - bx), Math.abs(left + w - bx));
  };

  /** Insert a static box into the left-sorted fixed list. */
  const addFixed = (b: FlowBox): void => {
    let lo = 0;
    let hi = fixedBoxes.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (fixedBoxes[mid].left < b.left) lo = mid + 1;
      else hi = mid;
    }
    fixedBoxes.splice(lo, 0, b);
    maxFixedW = Math.max(maxFixedW, b.right - b.left);
  };

  /** Fold `e` into a chip; `asFixed`: the chip is placed during the fixed phase. */
  const hide = (e: Entry, asFixed = false): void => {
    const it = e.item;
    const ref: HiddenRef = { source: e.source, key: it.key, label: it.label ?? it.key, time: it.time };
    const own = `${it.time}:${it.side}`;
    let chip = chipsByKey.get(own);
    if (!chip) {
      // A chip on a neighbouring bar that this one would sit on anyway.
      const L = boxLeft(it);
      const R = L + it.w;
      const prefCentre = preferredTop(it) + it.h / 2;
      for (let i = chips.length - 1; i >= 0; i -= 1) {
        const c = chips[i];
        if (c.side !== it.side) continue;
        if (c.left - opts.chipW <= R && L <= c.left + c.w + opts.chipW && Math.abs(c.top + c.h / 2 - prefCentre) <= opts.maxStack * 2) {
          chip = c;
          break;
        }
        if (c.x + opts.chipW * 2 < L) break;
      }
    }
    if (!chip) {
      const left = it.x - opts.chipW / 2;
      const gap = it.gap ?? DEFAULT_GAP;
      const anchor = it.fixed ? (it.side === 'below' ? it.anchorY + it.h / 2 : it.anchorY - it.h / 2) : it.anchorY;
      const pref = it.side === 'below' ? anchor + gap : anchor - gap - opts.chipH;
      const top = slot(left, left + opts.chipW, pref, opts.chipH, it.side);
      chip = { key: `chip:${own}`, time: it.time, side: it.side, x: it.x, left, top, w: opts.chipW, h: opts.chipH, hidden: [] };
      chips.push(chip);
      lead('chip', chip.key, it.x, it.leaderY ?? anchor, gap, it.side, chip, undefined);
      if (asFixed) addFixed({ left, top, right: left + opts.chipW, bottom: top + opts.chipH, bx: it.x });
      else pushFlow(left, top, opts.chipW, opts.chipH, it.x);
    }
    if (!chipsByKey.has(own)) chipsByKey.set(own, chip);
    chip.hidden.push(ref);
  };

  // Candles: obstacles for everything that moves (fixed boxes keep their price).
  for (const c of candleBoxes) addFixed({ left: c.left, top: c.top, right: c.left + c.w, bottom: c.top + c.h, bx: c.left + c.w / 2 });

  // Fixed boxes that lost to another fixed box: chip them where they stood.
  for (const e of overflow) hide(e, true);
  // Bar sides where a FREE box overflowed: everything after it there folds too,
  // so nothing lower in priority takes a slot nearer the candle than one that
  // was refused.
  const folded = new Set<string>();

  for (const e of free) {
    const it = e.item;
    const L = boxLeft(it);
    const R = L + it.w;
    const pref = preferredTop(it);
    // A bar side that already overflowed keeps folding into its chip: nothing
    // later on it may claim a slot further out than the chip.
    const own = `${it.time}:${it.side}`;
    if (folded.has(own)) {
      hide(e);
      continue;
    }
    const top = slot(L, R, pref, it.h, it.side);
    if (Math.abs(top - pref) > opts.maxStack) {
      folded.add(own);
      hide(e);
      continue;
    }
    pushFlow(L, top, it.w, it.h, it.x);
    record(e, L, top);
  }

  return { placed, bySource, chips, leaders, candles: candleBoxes };
}

export function chipText(chip: Pick<LayoutChip, 'hidden'>): string {
  const n = chip.hidden.length;
  return n > 99 ? '99+' : `+${n}`;
}

// ------------------------------------------------------------- text measuring

export type TextMeasurer = (text: string, font: string) => number;

let measureCtx: CanvasRenderingContext2D | null | undefined;
const measureCache = new Map<string, number>();

function fontPx(font: string): number {
  const m = /(\d+(?:\.\d+)?)px/.exec(font);
  return m ? Number(m[1]) : 10;
}

/** Without a canvas (jsdom): ~0.6em a character, ~1.3em for anything outside Latin-1 (emoji). */
export function estimateTextWidth(text: string, font: string): number {
  const px = fontPx(font);
  let w = 0;
  for (const ch of text) w += ch.codePointAt(0)! > 0xff ? px * 1.3 : px * 0.6;
  return Math.ceil(w);
}

let measurerOverride: TextMeasurer | null = null;

/** Tests: swap the measurer (null restores the canvas one). */
export function setTextMeasurer(fn: TextMeasurer | null): void {
  measurerOverride = fn;
  measureCache.clear();
}

/** Width of `text` in `font` (CSS px), measured once on a shared canvas and cached. */
export function measureText(text: string, font: string): number {
  if (measurerOverride) return measurerOverride(text, font);
  const k = `${font}\u0000${text}`;
  const hit = measureCache.get(k);
  if (hit !== undefined) return hit;
  if (measureCtx === undefined) {
    measureCtx = null;
    try {
      const jsdom = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
      if (!jsdom && typeof document !== 'undefined') measureCtx = document.createElement('canvas').getContext('2d');
    } catch {
      measureCtx = null;
    }
  }
  let w: number;
  if (measureCtx) {
    measureCtx.font = font;
    w = measureCtx.measureText(text).width;
  } else {
    w = estimateTextWidth(text, font);
  }
  if (measureCache.size > 5000) measureCache.clear();
  measureCache.set(k, w);
  return w;
}

// ------------------------------------------------------------- registry

export interface LayoutBar {
  open: number;
  high: number;
  low: number;
  close: number;
}

/** What a provider gets to turn its items into boxes. */
export interface LayoutContext {
  timeToX(time: number): number | null;
  priceToY(price: number): number | null;
  /** The displayed bar at `time` (seconds), null when the series has none there. */
  bar(time: number): LayoutBar | null;
  /** Items outside [fromTime, toTime] (seconds) are off screen and may be skipped. */
  fromTime: number;
  toTime: number;
  barSpacing: number;
  paneWidth: number;
  paneHeight: number;
  measure: TextMeasurer;
  /** Visible bars (plus the left margin), as obstacles. */
  candles?(): LayoutCandle[];
}

export type LayoutProvider = (ctx: LayoutContext) => readonly LayoutItem[];

/** Where the registry reads the view from (lightweight-charts in the app, a fake in tests). */
export interface LayoutEnv {
  /** Changes whenever anything that moves a pixel changes (range, zoom, scale, size). */
  viewKey(): string;
  context(): LayoutContext | null;
}

export type LayoutHit =
  | { kind: 'item'; source: string; key: string; item: LayoutItem; box: LayoutBox }
  | { kind: 'chip'; chip: LayoutChip };

export interface DebugBox extends LayoutBox {
  source: string;
  key: string;
  kind: 'item' | 'chip';
  hidden?: number;
}

const EMPTY_RESULT: LayoutResult = { placed: [], bySource: new Map(), chips: [], leaders: [], candles: [] };

export class MarkerLayoutRegistry {
  private _providers = new Map<string, { provider: LayoutProvider; token: number }>();
  private _token = 0;
  private _version = 0;
  private _key: string | null = null;
  private _result: LayoutResult = EMPTY_RESULT;
  private _opts: LayoutOptions;
  private _listeners = new Set<() => void>();
  /** Computations run (tests and the debug hook read it). */
  computeCount = 0;

  constructor(private _env: LayoutEnv, opts: Partial<LayoutOptions> = {}) {
    this._opts = { ...DEFAULT_LAYOUT_OPTIONS, ...opts };
  }

  /**
   * Add (or replace) the provider for `id`. Returns the disposer, which only
   * removes this registration (a newer one under the same id survives it).
   */
  register(id: string, provider: LayoutProvider): () => void {
    const token = ++this._token;
    this._providers.set(id, { provider, token });
    this.invalidate();
    return () => {
      const cur = this._providers.get(id);
      if (cur && cur.token === token) {
        this._providers.delete(id);
        this.invalidate();
      }
    };
  }

  has(id: string): boolean {
    return this._providers.has(id);
  }

  /** An item set changed: the next layout() recomputes. */
  invalidate(): void {
    this._version += 1;
    for (const fn of this._listeners) fn();
  }

  /** Told on every invalidate (the chip primitive repaints on it). */
  onInvalidate(fn: () => void): () => void {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  setOptions(opts: Partial<LayoutOptions>): void {
    this._opts = { ...this._opts, ...opts };
    this.invalidate();
  }

  /** The layout for the current view; recomputed only when the view or an item set changed. */
  layout(): LayoutResult {
    const view = this._env.viewKey();
    const key = `${this._version}|${view}`;
    if (key === this._key) return this._result;
    const ctx = this._env.context();
    if (!ctx) {
      this._key = key;
      this._result = EMPTY_RESULT;
      return this._result;
    }
    const sources: LayoutSourceInput[] = [];
    for (const [id, { provider }] of this._providers) {
      let items: readonly LayoutItem[] = [];
      try {
        items = provider(ctx);
      } catch (err) {
        // One broken source must not take every marker with it.
        console.error(`[markerLayout] provider ${id} failed`, err);
      }
      if (items.length > 0) sources.push({ id, rank: SOURCE_RANK[id] ?? 100, items });
    }
    // Up to opts.maxStack, less on a short pane (30% of it, never under 48px).
    const maxStack = Math.min(this._opts.maxStack, ctx.paneHeight > 0 ? Math.max(48, ctx.paneHeight * 0.3) : this._opts.maxStack);
    this._result = computeMarkerLayout(sources, { ...this._opts, maxStack }, ctx.candles?.() ?? []);
    this._key = key;
    this.computeCount += 1;
    return this._result;
  }

  /** The last computed layout, without recomputing (hover between frames). */
  last(): LayoutResult {
    return this._result;
  }

  /** `id`'s placed boxes for the current view. */
  placedFor(id: string): readonly PlacedItem[] {
    return this.layout().bySource.get(id) ?? EMPTY_PLACED;
  }

  /** Box or chip under a point, from the last layout. Chips win (they sit on top). */
  hitTest(x: number, y: number, slop = 2): LayoutHit | null {
    const r = this._result;
    for (let i = r.chips.length - 1; i >= 0; i -= 1) {
      const c = r.chips[i];
      if (x >= c.left - slop && x <= c.left + c.w + slop && y >= c.top - slop && y <= c.top + c.h + slop) return { kind: 'chip', chip: c };
    }
    for (let i = r.placed.length - 1; i >= 0; i -= 1) {
      const p = r.placed[i];
      if (x >= p.left - slop && x <= p.left + p.w + slop && y >= p.top - slop && y <= p.top + p.h + slop) {
        return { kind: 'item', source: p.source, key: p.item.key, item: p.item, box: { left: p.left, top: p.top, w: p.w, h: p.h } };
      }
    }
    return null;
  }

  /** Candle spans and leaders of the current layout (the DEV hook). */
  debugExtras(): { candles: LayoutBox[]; leaders: LayoutLeader[] } {
    const r = this.layout();
    return { candles: r.candles, leaders: r.leaders };
  }

  /** Every placed box and chip (the DEV hook and the headless overlap check). */
  debugBoxes(): DebugBox[] {
    const r = this.layout();
    const out: DebugBox[] = r.placed.map((p) => ({ source: p.source, key: p.item.key, kind: 'item' as const, left: p.left, top: p.top, w: p.w, h: p.h }));
    for (const c of r.chips) out.push({ source: 'chip', key: c.key, kind: 'chip', left: c.left, top: c.top, w: c.w, h: c.h, hidden: c.hidden.length });
    return out;
  }

  dispose(): void {
    this._providers.clear();
    this._listeners.clear();
    this._result = EMPTY_RESULT;
    this._key = null;
  }
}

/**
 * A primitive's handle on the registry: registered only while the primitive is
 * attached AND has a registry, so a detached (toggled off, Clean-suppressed)
 * source frees its slots on the next frame.
 */
export class MarkerLayoutClient {
  private _reg: MarkerLayoutRegistry | null = null;
  private _attached = false;
  private _dispose: (() => void) | null = null;

  constructor(readonly id: string, private _provider: LayoutProvider) {}

  setRegistry(reg: MarkerLayoutRegistry | null): void {
    if (reg === this._reg) return;
    this._reg = reg;
    this._sync();
  }

  registry(): MarkerLayoutRegistry | null {
    return this._reg;
  }

  setAttached(on: boolean): void {
    if (on === this._attached) return;
    this._attached = on;
    this._sync();
  }

  /** True when positions come from the registry (else the primitive lays itself out). */
  active(): boolean {
    return this._dispose !== null;
  }

  invalidate(): void {
    if (this._dispose) this._reg?.invalidate();
  }

  /** This source's boxes for the current view, or null when not using the registry. */
  placed(): readonly PlacedItem[] | null {
    if (!this._dispose || !this._reg) return null;
    return this._reg.placedFor(this.id);
  }

  private _sync(): void {
    this._dispose?.();
    this._dispose = null;
    if (this._reg && this._attached) this._dispose = this._reg.register(this.id, this._provider);
  }
}

// ------------------------------------------------------------- lightweight-charts env

/** Visible-range margin (bars) laid out off screen to the left, so panning does not reshuffle the left edge. */
const MARGIN_BARS = 12;

export function chartLayoutEnv(chart: any, series: any): LayoutEnv {
  const ts = () => chart.timeScale();
  return {
    viewKey(): string {
      try {
        const t = ts();
        const lr = t.getVisibleLogicalRange();
        const size = chart.paneSize?.(0) ?? { width: 0, height: 0 };
        const p0 = series.coordinateToPrice(0);
        const p1 = series.coordinateToPrice(100);
        return `${lr?.from}|${lr?.to}|${t.width()}|${size.height}|${p0}|${p1}`;
      } catch {
        return 'detached';
      }
    },
    context(): LayoutContext | null {
      let t: any;
      try {
        t = ts();
      } catch {
        return null;
      }
      const lr = t.getVisibleLogicalRange();
      let fromTime = -Infinity;
      let toTime = Infinity;
      const barSpacing = t.options?.()?.barSpacing ?? 6;
      const candles = (): LayoutCandle[] => {
        const out: LayoutCandle[] = [];
        if (!lr) return out;
        const halfW = candleHalfWidth(barSpacing);
        const last = Math.ceil(lr.to) + 1;
        for (let i = Math.max(0, Math.floor(lr.from) - MARGIN_BARS); i <= last; i += 1) {
          const d = series.dataByIndex(i, 0 /* None */);
          if (!d) {
            if (i > lr.to) break;
            continue;
          }
          if (typeof d.high !== 'number' || typeof d.time !== 'number') continue;
          const x = t.timeToCoordinate(d.time);
          const hy = series.priceToCoordinate(d.high);
          const ly = series.priceToCoordinate(d.low);
          if (x === null || hy === null || ly === null) continue;
          out.push({ time: d.time, x, highY: hy, lowY: ly, halfW });
        }
        return out;
      };
      if (lr) {
        const a = series.dataByIndex(Math.max(0, Math.floor(lr.from) - MARGIN_BARS), 1 /* NearestRight */);
        if (a && typeof a.time === 'number') fromTime = a.time;
        // Null past the newest bar (right offset): everything to the end is in view.
        const b = series.dataByIndex(Math.ceil(lr.to) + 1, 0 /* None */);
        if (b && typeof b.time === 'number') toTime = b.time;
      }
      const size = chart.paneSize?.(0) ?? { width: t.width(), height: 0 };
      return {
        timeToX: (time: number) => t.timeToCoordinate(time),
        priceToY: (price: number) => series.priceToCoordinate(price),
        bar: (time: number) => {
          const idx = t.timeToIndex(time, false);
          if (idx === null || idx === undefined) return null;
          const d = series.dataByIndex(idx, 0 /* None */);
          if (!d || d.time !== time || typeof d.high !== 'number') return null;
          return { open: d.open, high: d.high, low: d.low, close: d.close };
        },
        fromTime,
        toTime,
        barSpacing,
        paneWidth: size.width,
        paneHeight: size.height,
        measure: measureText,
        candles,
      };
    },
  };
}

// ------------------------------------------------------------- tooltip + test hook

/** Hover text for a "+N" chip: what it hides, oldest bar first. */
export function markerChipTooltip(chip: Pick<LayoutChip, 'hidden'>, max = 12): { title: string; lines: string[] } {
  const n = chip.hidden.length;
  const lines = chip.hidden.slice(0, max).map((h) => h.label);
  if (n > max) lines.push(`+${n - max} more`);
  return { title: `${n} more marker${n === 1 ? '' : 's'} here (zoom in to see them)`, lines };
}

interface ExposedLayout {
  registry: MarkerLayoutRegistry;
  el: HTMLElement;
}

declare global {
  interface Window {
    /** DEV only: every chart's placed marker boxes and chips (tests/markers-no-overlap.spec.ts). */
    __uttMarkerBoxes?: () => { index: number; width: number; height: number; boxes: DebugBox[]; candles: LayoutBox[]; leaders: LayoutLeader[] }[];
  }
}

const exposed: ExposedLayout[] = [];

/**
 * DEV only: publish a chart's layout for headless checks, as
 * `window.__uttMarkerBoxes()` (all charts) and `data-marker-boxes` (count) on
 * its container. Returns the undo.
 */
export function exposeMarkerLayout(registry: MarkerLayoutRegistry, el: HTMLElement): () => void {
  const entry: ExposedLayout = { registry, el };
  exposed.push(entry);
  if (typeof window !== 'undefined') {
    window.__uttMarkerBoxes = () =>
      exposed.map((e, index) => {
        const boxes = e.registry.debugBoxes();
        const { candles, leaders } = e.registry.debugExtras();
        e.el.dataset.markerBoxes = String(boxes.length);
        return { index, width: e.el.clientWidth, height: e.el.clientHeight, boxes, candles, leaders };
      });
  }
  return () => {
    const i = exposed.indexOf(entry);
    if (i >= 0) exposed.splice(i, 1);
    delete el.dataset.markerBoxes;
  };
}
